import {ComponentNext, Link}        from "@Common/react/cst2";
import {Types}                      from "@Common/types";
import {cleanItem, itemEditor, ui}  from "@Common/ui-components/heavy/item-options/modules/expo-types/expo-types";
import {changeNestedValue}          from "@Common/utils/arr-path";
import {chain}                      from "@Common/utils/fs";

import {VariationsPricingTableModal}    from "./pricing-modal/pricing-modal";
import {MasterPriceTier, PricingRanges} from "./pricing-ranges";
import {VariationSelectionState}        from "./variations-state";
import {VariationsStock}                from "./variations-stock";
import {VariationsThumbnailsState}      from "./variations-thumbnails-state";
import {VariationSelectionUi}           from "./variations-ui";

type Item = Types.Apis.Payloads.Next.Item.Get.Response;

export const variationsModules = ({item, templateBack, templateOptions}:{
    item: Item,
    templateBack: Types.Apis.Payloads.Next.TemplateBack.GetTemplates.Response,
    templateOptions: Types.Apis.Models.TemplateOptions,
}) => [
    // state/logic module
    {
        name: "variationSelectionState",
        load: ({}, next:Link<any, any>) => VariationSelectionState({item, templateBack, templateOptions, next}),
        expo: [({module}, exported: any) => module ? {...exported, variationSelectionState: module} : exported],
    },

    // pricing modules
    {
        name: "variationsStock",
        requires: ["variationSelectionState"],
        load: (
            {variationSelectionState}:{variationSelectionState: ComponentNext<typeof VariationSelectionState>},
            next:Link<any, any>
        ) => VariationsStock({next, templateOptions, variationSelectionState}),
    },

    {
        name: "variationsPricing",
        requires: ["paperPrices", "quantity2", "variationSelectionState", "variationsStock"],
        load: ({paperPrices, quantity2, variationSelectionState, variationsStock}:{
            paperPrices: MasterPriceTier[],
            quantity2: any,
            variationSelectionState: ComponentNext<typeof VariationSelectionState>,
            variationsStock: ComponentNext<typeof VariationsStock>,
        }, next:Link<any, any>) => PricingRanges({
            next,
            paperPrices,
            quantity2,
            variationSelectionState,
            variationsStock,
        }),
    },

    {
        name: "variationsPricingTableModal",
        requires: ["variationsPricing", "variationSelectionState"],
        load: (
            {variationsPricing, variationSelectionState}:{
                variationsPricing: ComponentNext<typeof PricingRanges>,
                variationSelectionState: ComponentNext<typeof VariationSelectionState>,
            },
            next:Link<any, any>
        ) => (
            !variationSelectionState.enabled
            ? next(null)
            : VariationsPricingTableModal({
                next,
                priceRangeTiers: variationsPricing.tiers,
            })
        ),
    },

    // thumbnails modules
    {
        name: "variationsThumbnails",
        requires: ["variationSelectionState"],
        load: (
            {variationSelectionState}:{variationSelectionState: ComponentNext<typeof VariationSelectionState>},
            next:Link<any, any>
        ) => VariationsThumbnailsState({templateBack, variationSelectionState, next}),
        expo: [({module}, exported: any) => module ? {...exported, variationsThumbnails: module} : exported],
    },

    // order-editor modules
    {
        requires: ["variationSelectionState", "variationsPricing"],
        load: (
            {variationsPricing, variationSelectionState}:{
                variationsPricing: ComponentNext<typeof PricingRanges>,
                variationSelectionState: ComponentNext<typeof VariationSelectionState>
            },
            next:Link<any, any>
        ) => next({
            cleanItem: (
                !variationSelectionState.enabled
                ? null
                : ((item:Item) => changeNestedValue(item, ["selections", "options"], options => options?.filter(
                    option => option.dev_key !== "pick_pack_options"
                ) ?? []))
            ),
            itemEditor: !variationSelectionState.enabled ? null : {
                $name: "variations",
                changed: variationSelectionState.changed,
                prepareSave: (item:Item) => ({
                    newValue: chain(
                        item,
                        (item) => changeNestedValue(item, ["selections", "options"] as const, options => [
                            ...(options ?? []),
                            {
                                dev_key: "pick_pack_options",
                                name: setPickPackOptions(variationSelectionState.selection.matchingVariations[0].fieldValues, templateBack),
                            }
                        ]),
                        (item) => changeNestedValue(item, ["sku"], (sku) => ({
                            ...sku,
                            front: templateBack.id,
                            variation: variationSelectionState.selection.matchingVariations[0]?.variationIdTemplate,
                        })),
                    ),
                    onSaved: () => {},
                }),
                valid: variationSelectionState.selection.isValid && !!variationsPricing.matchedTier,
            },
            ui: variationSelectionState.enabled ? VariationSelectionUi({fields: variationSelectionState.fields}) : null,
        }),
        expo: [cleanItem, itemEditor, ui("variationSelection")],
    },
];

const setPickPackOptions = (variationSelectionState, templateBack) => {
    const options = [];
    for(const selectedOption in variationSelectionState) {
        const fieldId = selectedOption;
        const selectedOptionId = variationSelectionState[selectedOption];
        const field = templateBack.variationOptionFields.find((field) => field.id === fieldId);
        const option = field?.options?.find((option) => option.id === selectedOptionId);
        if (field?.type === "options") {
            options.push({
                type: field.type,
                uiLabel: field.label || field.name,
                uiValue: option?.label,
                vendorEmailCategory: field.emailCategory,
                vendorLabel: field.name,
                vendorValue: option?.value || option?.label
            })
        }
        if (field?.type === "vendor" && (selectedOptionId || field.value !== null) ) {
            options.push({
                type: field.type,
                vendorEmailCategory: field.emailCategory,
                vendorLabel: field.name,
                vendorValue: selectedOptionId || field.value,
            })
        }

    }
    for(const field of templateBack.variationOptionFields) {
        if(field.type === "proofing") {
            options.push({
                fieldName: field.proofingField.fieldName,
                surfaceName: field.proofingField.surfaceName,
                type: field.type,
                vendorEmailCategory: field.emailCategory,
                vendorLabel: field.name,
                vendorValue: ""
            })
        }
    }
    return JSON.stringify(options)
}
